import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import { GiCharacter } from 'react-icons/gi';
import { FaStar } from 'react-icons/fa';
import { io } from 'socket.io-client';
import apiFetch from '../../../utils/apiFetch';
import './Objectives.scss';

const PRIORITIES = ['gold', 'silver', 'bronze'];
const label = priority => `${priority[0].toUpperCase()}${priority.slice(1)} Star`;
const holderCount = objective => objective.assignments?.filter(a => !a.dismissedAt).length || 0;
const requestId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const blankEditor = (recipients, future = false) => ({ title: '', details: '', priority: 'bronze', rewardXp: 0, characterIds: recipients, future, requestId: requestId() });

async function readResponse(response) {
  const value = await response.json();
  if (!response.ok) throw Object.assign(new Error(value.message || 'Unable to save objectives.'), { status: response.status });
  return value;
}

export default function ObjectivesModal({ show, onHide, campaignId, characterId, isDm = false }) {
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);
  const [pending, setPending] = useState(null);
  const [notice, setNotice] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const sequence = useRef(0);
  const lock = useRef(false);
  const currentScope = useRef('');
  const scope = `${campaignId || ''}:${characterId || ''}:${isDm}`;
  currentScope.current = scope;
  const targetId = isDm ? selectedId : characterId;
  const endpoint = `/campaigns/${encodeURIComponent(campaignId || '')}/objectives`;

  const load = useCallback(async () => {
    if (!show || !campaignId || (!isDm && !characterId)) return;
    const version = ++sequence.current;
    setLoading(true); setError('');
    try {
      const suffix = targetId ? `?characterId=${encodeURIComponent(targetId)}` : '';
      const value = await readResponse(await apiFetch(endpoint + suffix, { cache: 'no-store' }));
      if (sequence.current === version) setData(value);
    } catch (err) { if (sequence.current === version) setError(err.message); }
    finally { if (sequence.current === version) setLoading(false); }
  }, [show, campaignId, characterId, isDm, targetId, endpoint]);
  const reload = useRef(load); reload.current = load;

  useEffect(() => {
    setSelectedId(null); setEditor(null); setPending(null); setSaveError(''); setNotice(''); setData(null); setDeleteTarget(null); setCompleteTarget(null);
  }, [show, scope]);

  useEffect(() => {
    setData(null);
    load();
    return () => { sequence.current++; };
  }, [load]);

  useEffect(() => {
    if (!show || !campaignId) return undefined;
    // Fetch after joining and on reconnect, recovering updates missed while offline.
    const socket = io(process.env.REACT_APP_API_URL || undefined, { withCredentials: true });
    const join = () => socket.emit('campaign:join', campaignId, result => {
      if (result?.status === 'joined') reload.current();
    });
    socket.on('connect', join);
    socket.on('campaign:objectives:update', () => reload.current());
    const refresh = () => reload.current();
    window.addEventListener('focus', refresh);
    return () => { window.removeEventListener('focus', refresh); socket.disconnect(); };
  }, [show, campaignId]);

  const save = async operation => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setSaveError(''); setNotice('');
    const startedScope = scope;
    try {
      const result = await readResponse(await apiFetch(operation.url, {
        method: operation.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(operation.body),
      }));
      if (currentScope.current !== startedScope) return;
      setEditor(null); setPending(null); setNotice(operation.body.action === 'publish' ? 'Objective assigned to players.' : operation.body.action === 'restore' ? 'Objective re-added. The original remains in Past Objectives.' : operation.body.action === 'complete'
        ? result.award ? `Objective completed. ${result.award.share.toLocaleString()} XP awarded to each of ${result.award.recipients.length} holders.` : 'Objective completed.'
        : operation.notice || 'Objective saved.');
      setDeleteTarget(null); setCompleteTarget(null);
      window.dispatchEvent(new CustomEvent('realmtracker:objectives-changed', { detail: { campaignId, characterId: targetId } }));
      await reload.current();
    } catch (err) {
      if (currentScope.current !== startedScope) return;
      // Keep the exact creation identity and payload after an uncertain response.
      if ((operation.method === 'POST' || ['restore', 'publish'].includes(operation.body.action)) && (!err.status || err.status >= 500)) setPending(operation);
      else setPending(null);
      setSaveError(err.message);
      if (err.status === 409) await reload.current();
    } finally {
      lock.current = false;
      if (currentScope.current === startedScope) setBusy(false);
    }
  };

  const startAdd = (future = false) => {
    setSaveError(''); setNotice(''); setPending(null);
    setEditor(blankEditor(future === true ? [] : targetId ? [String(targetId)] : [], future === true));
  };
  const startEdit = objective => {
    setSaveError(''); setNotice(''); setPending(null);
    setEditor({ ...objective, restoring: Boolean(objective.isPast), requestId: requestId(), characterIds: isDm ? objective.assignments.filter(a => (objective.isPast || !a.dismissedAt) && data.characters.some(c => c.id === a.characterId)).map(a => a.characterId) : [String(characterId)], priority: objective.isPast || objective.future ? objective.priority : 'bronze' });
  };
  const submit = (event, publish = false) => {
    event.preventDefault();
    if (pending) { save(pending); return; }
    const editing = Boolean(editor.id);
    const body = editing ? {
      action: publish ? 'publish' : editor.restoring ? 'restore' : 'content', revision: editor.revision, title: editor.title, details: editor.details,
      ...(editor.restoring ? { requestId: editor.requestId } : {}),
      ...(isDm ? { characterIds: editor.characterIds, priority: editor.priority, rewardXp: Number(editor.rewardXp || 0) } : {}),
    } : { requestId: editor.requestId, title: editor.title, details: editor.details, priority: editor.priority, characterIds: editor.characterIds,
      ...(isDm ? { rewardXp: Number(editor.rewardXp || 0), future: editor.future === true } : {}) };
    save({ url: endpoint + (editing ? `/${editor.id}` : ''), method: editing ? 'PATCH' : 'POST', body });
  };
  const changePriority = (objective, priority) => save({
    url: `${endpoint}/${objective.id}`, method: 'PATCH',
    body: { action: 'priority', revision: objective.revision, characterId: String(targetId), priority },
  });
  const markRead = objective => {
    if (isDm || !objective.unread) return;
    save({
      url: `${endpoint}/${objective.id}`, method: 'PATCH', notice: 'Objective marked as read.',
      body: { action: 'read', revision: objective.revision, characterId: String(targetId) },
    });
  };
  const confirmDelete = objective => save({
    url: `${endpoint}/${objective.id}`, method: 'DELETE', notice: isDm ? objective.isPast ? 'Removed from Past Objectives.' : 'Objective moved to Past Objectives.' : 'Objective deleted.',
    body: { revision: objective.revision, ...(!isDm || (objective.isPast && targetId) ? { characterId: String(targetId) } : {}), ...(isDm && objective.isPast ? { fromPast: true } : {}) },
  });
  const confirmComplete = objective => save({
    url: `${endpoint}/${objective.id}`, method: 'PATCH',
    body: { action: 'complete', revision: objective.revision },
  });
  const close = () => { if (!lock.current) onHide(); };
  const selected = data?.characters?.find(c => c.id === selectedId);
  const ready = Boolean(campaignId && (isDm || characterId));

  const renderObjective = objective => <article key={objective.id} className="modern-card p-3 objectives-modal__objective">
            <div className="d-flex align-items-start gap-2">
              <FaStar className={`objectives-modal__star objectives-modal__star--${objective.priority}`} aria-label={label(objective.priority)} role="img" />
              <h3 className="fs-5 flex-grow-1 objectives-modal__name">{objective.title}</h3>
              {objective.completedAt && <Badge bg="success">Completed</Badge>}
              {objective.isPast && !objective.completedAt && <Badge bg="secondary">Past</Badge>}
              {objective.future && <Badge bg="info" text="dark">Future</Badge>}
              {!isDm && objective.unread && <Badge bg="info" text="dark">New</Badge>}
              {(objective.assignedByDm || objective.source === 'dm') && <Badge bg="secondary">DM</Badge>}
            </div>
            <details className="mb-3" onToggle={event => { if (event.currentTarget.open) markRead(objective); }}>
              <summary>Details</summary>
              <p className="objectives-modal__details mt-2 mb-0">{objective.details || 'No additional details.'}</p>
              {!isDm && objective.unread && <Button size="sm" variant="outline-light" disabled={busy} className="mt-2" onClick={() => markRead(objective)}>Mark as read</Button>}
            </details>
            {isDm && objective.isPast && <p className="small mb-2">Recipients: {objective.assignments.length ? objective.assignments.map(a => data.characters.find(c => c.id === a.characterId)?.name || a.characterId).join(', ') : 'Select recipients in Edit to re-add this objective.'}</p>}
            {objective.restoredFrom && <p className="small mb-2">Re-added from a past objective.</p>}
            {objective.rewardXp > 0 && <p className="small mb-3">{objective.completedAt ? `${objective.awardedXp || 0} XP awarded ${isDm && !targetId ? 'per holder' : 'to this character'}` : `${objective.rewardXp.toLocaleString()} XP reward per holder`}</p>}
            <div className="d-flex align-items-center flex-wrap gap-2">
              <Form.Label className="mb-0" htmlFor={`objective-priority-${objective.id}`}>Priority</Form.Label>
              <Form.Select id={`objective-priority-${objective.id}`} aria-label={`Priority for ${objective.title}`} className="objectives-modal__priority" size="sm" value={objective.priority} disabled={busy || loading || objective.future || objective.isPast || Boolean(objective.completedAt)} onChange={e => changePriority(objective, e.target.value)}>
                {PRIORITIES.map(p => <option key={p} value={p}>{label(p)}</option>)}
              </Form.Select>
              {isDm && !objective.isPast && !objective.future && <Button size="sm" variant="outline-success" className="ms-auto" disabled={busy || loading || Boolean(objective.completedAt)} onClick={() => { setCompleteTarget(objective); setDeleteTarget(null); setSaveError(''); }}>{objective.completedAt ? 'Done' : 'Mark Done'}</Button>}
              {(isDm || (objective.source === 'player' && String(objective.creatorCharacterId) === String(characterId))) && <Button size="sm" variant="outline-light" className={isDm && !objective.isPast ? '' : 'ms-auto'} disabled={busy || loading || Boolean(objective.completedAt && !(isDm && objective.isPast))} onClick={() => startEdit(objective)}>Edit</Button>}
              {(isDm || !(!objective.completedAt && (objective.assignedByDm ?? objective.source === 'dm') && objective.rewardXp > 0)) && <Button size="sm" variant="outline-danger" disabled={busy || loading} aria-label={`Delete ${objective.title}`} onClick={() => { setDeleteTarget(objective); setCompleteTarget(null); setSaveError(''); }}>Delete</Button>}
            </div>
            {completeTarget?.id === objective.id && <Alert variant="info" className="mt-3 mb-0">
              <p>Mark this objective done for all {holderCount(objective)} holders?
                {objective.rewardXp > 0 ? ` Award ${objective.rewardXp.toLocaleString()} XP to each holder (${(objective.rewardXp * holderCount(objective)).toLocaleString()} XP total).` : ' No XP reward is set.'}
              </p>
              <div className="d-flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => confirmComplete(completeTarget)}>{busy ? 'Completing…' : 'Confirm Done'}</Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => setCompleteTarget(null)}>Cancel</Button>
              </div>
            </Alert>}
            {deleteTarget?.id === objective.id && <Alert variant="warning" className="mt-3 mb-0">
              <p>{isDm ? objective.isPast ? 'Remove this objective from Past Objectives? It will no longer be available to re-add.' : objective.future ? 'Move this future objective to Past Objectives?' : 'Move this objective to Past Objectives? It will be removed from players’ lists.' : 'Remove this objective from your list? Other players will keep their copies.'}</p>
              {objective.completedAt && <p>XP already awarded will be kept.</p>}
              <div className="d-flex gap-2">
                <Button size="sm" variant="danger" disabled={busy} onClick={() => confirmDelete(deleteTarget)}>{busy ? 'Deleting…' : 'Confirm Delete'}</Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => setDeleteTarget(null)}>Cancel</Button>
              </div>
            </Alert>}
          </article>;

  return <Modal show={show} onHide={close} size="lg" scrollable centered className="dnd-modal zombies-dm-resource-modal objectives-modal">
    <Modal.Header closeButton={!busy}>
      <Modal.Title as="h2">{editor ? (editor.future ? editor.id ? 'Edit Future Objective' : 'Add Future Objective' : editor.restoring ? 'Re-add Objective' : editor.id ? 'Edit Objective' : 'Add Objective') : selected ? `${selected.name} — Objectives` : 'Objectives'}</Modal.Title>
      {!editor && <Button className="ms-auto me-3 flex-shrink-0" size="sm" onClick={startAdd} disabled={busy || !ready || !data || Boolean(error)}>Add Objective</Button>}
    </Modal.Header>
    <Modal.Body>
      {!ready && <Alert variant="info">Join a campaign to use objectives.</Alert>}
      {error && <Alert variant="danger">{error} <Button size="sm" variant="outline-danger" onClick={load} disabled={loading}>Retry</Button></Alert>}
      {saveError && <Alert variant="danger">{saveError}{pending && <div>The result is unconfirmed. Retry saving to recover the same objective safely.</div>}</Alert>}
      {notice && <Alert variant="success" role="status">{notice}</Alert>}
      {loading && <div role="status" className="mb-3"><Spinner animation="border" size="sm" /> Loading objectives…</div>}
      {editor ? <Form onSubmit={submit}>
        {editor.restoring && <Alert variant="info">Saving creates a new active objective and keeps the original in Past Objectives. XP is awarded only when the new objective is marked done.</Alert>}
        {editor.future && <Alert variant="info">Future objectives are visible only to the DM. Recipients are optional until you choose Assign to Players.</Alert>}
        <fieldset disabled={busy || Boolean(pending)}>
          <Form.Group className="mb-3" controlId="objective-title">
            <Form.Label>Title</Form.Label>
            <Form.Control required maxLength={200} value={editor.title} onChange={e => setEditor({ ...editor, title: e.target.value })} autoFocus />
          </Form.Group>
          <Form.Group className="mb-3" controlId="objective-details">
            <Form.Label>Details (optional)</Form.Label>
            <Form.Control as="textarea" rows={4} maxLength={10000} value={editor.details} onChange={e => setEditor({ ...editor, details: e.target.value })} />
          </Form.Group>
          {(!editor.id || isDm) && <Form.Group className="mb-3" controlId="objective-initial-priority">
            <Form.Label>{editor.id && !editor.restoring && !editor.future ? 'Initial priority for new recipients' : 'Priority'}</Form.Label>
            <Form.Select value={editor.priority} onChange={e => setEditor({ ...editor, priority: e.target.value })}>
              {PRIORITIES.map(p => <option value={p} key={p}>{label(p)}</option>)}
            </Form.Select>
            {editor.id && !editor.restoring && !editor.future && <Form.Text>Existing recipients keep their current priority.</Form.Text>}
          </Form.Group>}
          {isDm && <fieldset className="mb-3">
            <legend className="fs-6">Recipients</legend>
            <div className="objectives-modal__recipients">
              {(data?.characters || []).map(c => <Form.Check
                key={c.id} id={`objective-recipient-${c.id}`} label={c.name}
                checked={editor.characterIds.includes(c.id)}
                onChange={() => setEditor({ ...editor, characterIds: editor.characterIds.includes(c.id) ? editor.characterIds.filter(id => id !== c.id) : [...editor.characterIds, c.id] })}
              />)}
            </div>
            {!data?.characters?.length && <p>No active player characters in this campaign.</p>}
          </fieldset>}
          {isDm && <Form.Group className="mb-3" controlId="objective-reward-xp">
            <Form.Label>Experience reward (XP per holder)</Form.Label>
            <Form.Control type="number" min="0" max="10000000" step="1" required value={editor.rewardXp ?? 0} onChange={e => setEditor({ ...editor, rewardXp: e.target.value })} />
            <Form.Text>Each current objective holder receives this full amount when marked done. Set 0 for no XP reward.</Form.Text>
            {Number(editor.rewardXp) > 0 && editor.characterIds.length > 0 && <p className="small mt-2 mb-0">
              {Number(editor.rewardXp).toLocaleString()} XP each · {editor.characterIds.length} holders · {(Number(editor.rewardXp) * editor.characterIds.length).toLocaleString()} XP total
            </p>}
          </Form.Group>}
        </fieldset>
        <div className="d-flex flex-wrap justify-content-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={() => { setEditor(null); setPending(null); setSaveError(''); }}>Cancel</Button>
          <Button type="submit" disabled={busy || (!pending && (!editor.title.trim() || (!editor.future && !editor.characterIds.length)))}>{busy ? 'Saving…' : pending ? 'Retry Save' : editor.future ? 'Save Future Objective' : editor.restoring ? 'Re-add Objective' : 'Save Objective'}</Button>
          {editor.future && editor.id && !pending && <Button variant="success" disabled={busy || !editor.title.trim() || !editor.characterIds.length} onClick={event => { if (event.currentTarget.form.reportValidity()) submit(event, true); }}>Assign to Players</Button>}
        </div>
      </Form> : <>
        {isDm && selectedId && <Button variant="outline-light" size="sm" className="mb-3" disabled={busy} onClick={() => { setSelectedId(null); setSaveError(''); setNotice(''); }}>Back to players</Button>}
        {isDm && data && <h3 className="fs-5">Active Objectives</h3>}
        {isDm && !selectedId && data && <ListGroup>
          {data.characters.map(c => <ListGroup.Item action key={c.id} className="resource-card objectives-modal__player" onClick={() => { setSelectedId(c.id); setNotice(''); }}>
            {c.image ? <img src={c.image} alt="" className="objectives-modal__portrait" /> : <GiCharacter size={40} aria-hidden="true" />}
            <span className="objectives-modal__name">{c.name}</span>
            <Badge bg="secondary">{c.count} {c.count === 1 ? 'Objective' : 'Objectives'}</Badge>
          </ListGroup.Item>)}
          {!data.characters.length && <p className="mb-0">No active player characters in this campaign.</p>}
        </ListGroup>}
        {(!isDm || selectedId) && data && <div className="d-grid gap-3">
          {!data.objectives.length && <p>No objectives yet. Use Add Objective to create one.</p>}
          {data.objectives.map(renderObjective)}
        </div>}
        {isDm && data && <section className="mt-4 pt-3 border-top" aria-label="Future objectives">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
            <h3 className="fs-5 mb-0">Future Objectives</h3>
            <Button size="sm" disabled={busy || !ready || Boolean(error)} onClick={() => startAdd(true)}>Add Future Objective</Button>
          </div>
          <div className="d-grid gap-3">{(data.futureObjectives || []).map(renderObjective)}</div>
          {!data.futureObjectives?.length && <p>No future objectives yet. Prepare quests here before assigning them.</p>}
        </section>}
        {isDm && data && <section className="mt-4 pt-3 border-top" aria-label="Past objectives">
          <h3 className="fs-5">Past Objectives</h3>
          <div className="d-grid gap-3">{(data.pastObjectives || []).map(renderObjective)}</div>
          {!data.pastObjectives?.length && <p>No past objectives yet.</p>}
        </section>}
      </>}
    </Modal.Body>
    {!editor && <Modal.Footer><span className="me-auto" role="status">{busy ? 'Saving…' : ''}</span><Button variant="secondary" onClick={close} disabled={busy}>Close</Button></Modal.Footer>}
  </Modal>;
}
